/**
 * Notification Hooks
 *
 * React Query hooks and WebSocket integration for notifications.
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { parseApiError, getErrorMessage } from '@/lib/api-errors';
import { getAccessToken, triggerAuthFailure } from '@/lib/api-client';
import { socketClient } from '@/lib/websocket/socket-client';
import type { NotificationPayload, SessionPayload } from '@/lib/websocket/events';
import {
  useNotifications as useNotificationStore,
  useUnreadNotificationCount,
  useNotificationActions,
  useBoundStore,
} from '@/stores';
import {
  fetchNotifications,
  fetchNotificationStats,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
  dismissNotification,
  dismissAllNotifications,
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from '../services/notification.service';
import { stripHtml } from '../utils/stripHtml';
import { deriveActionUrl } from '../utils/deriveActionUrl';
import type { NotificationFilters } from '../types';

interface NotificationQueryOptions {
  enabled?: boolean;
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters?: NotificationFilters) => [...notificationKeys.lists(), filters] as const,
  stats: () => [...notificationKeys.all, 'stats'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

// =============================================================================
// NOTIFICATION HOOKS
// =============================================================================

/**
 * Fetch notifications with filters
 */
export function useNotificationsQuery(
  filters?: NotificationFilters,
  options?: NotificationQueryOptions,
) {
  const { setNotifications, setLoading, setError } = useNotificationActions();
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: notificationKeys.list(filters),
    queryFn: () => fetchNotifications(filters),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Sync with store
  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(query.isLoading);
    if (query.error) {
      setError(getErrorMessage(parseApiError(query.error)));
    } else {
      setError(null);
    }
    if (query.data) {
      setNotifications(query.data.map((n) => ({ ...n, read: n.read ?? false })));
    }
  }, [enabled, query.data, query.isLoading, query.error, setNotifications, setLoading, setError]);

  return query;
}

/**
 * Fetch notification statistics
 */
export function useNotificationStats(options?: NotificationQueryOptions) {
  return useQuery({
    queryKey: notificationKeys.stats(),
    queryFn: fetchNotificationStats,
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch unread count
 */
export function useUnreadCountQuery(options?: NotificationQueryOptions) {
  const { setUnreadCount } = useNotificationActions();
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: fetchUnreadCount,
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: enabled ? 60 * 1000 : false, // Refetch every minute
  });

  useEffect(() => {
    if (!enabled) {
      setUnreadCount(0);
      return;
    }

    if (query.data !== undefined) {
      setUnreadCount(query.data);
    }
  }, [enabled, query.data, setUnreadCount]);

  return query;
}

/**
 * Mark notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const { markAsRead: markAsReadStore } = useNotificationActions();

  return useMutation({
    mutationFn: markAsRead,

    onMutate: async (id) => {
      // Optimistic update
      markAsReadStore(id);
    },

    onError: (error) => {
      // Revert on error - refetch to get correct state
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError));
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { markAllAsRead: markAllAsReadStore } = useNotificationActions();

  return useMutation({
    mutationFn: markAllAsRead,

    onMutate: async () => {
      markAllAsReadStore();
    },

    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError));
    },

    onSuccess: () => {
      toast.success('All notifications marked as read');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Dismiss a notification (SAP-aligned soft-delete)
 */
export function useDismissNotification() {
  const queryClient = useQueryClient();
  const { removeNotification } = useNotificationActions();

  return useMutation({
    mutationFn: dismissNotification,

    onMutate: async (id) => {
      removeNotification(id);
    },

    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError));
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Dismiss all notifications (SAP-aligned soft-delete)
 */
export function useDismissAllNotifications() {
  const queryClient = useQueryClient();
  const { clearAll } = useNotificationActions();

  return useMutation({
    mutationFn: dismissAllNotifications,

    onMutate: async () => {
      clearAll();
    },

    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError));
    },

    onSuccess: () => {
      toast.success('All notifications dismissed');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

// =============================================================================
// PREFERENCES HOOKS
// =============================================================================

/**
 * Fetch notification preferences
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: fetchNotificationPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update notification preferences
 */
export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNotificationPreferences,

    onError: (error) => {
      const apiError = parseApiError(error);
      toast.error(getErrorMessage(apiError));
    },

    onSuccess: () => {
      toast.success('Preferences updated');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
    },
  });
}

// =============================================================================
// WEBSOCKET HOOK
// =============================================================================

/**
 * Hook for real-time notification updates via WebSocket
 */
export function useRealTimeNotifications(options?: NotificationQueryOptions) {
  const { addNotification, setUnreadCount } = useNotificationActions();
  const queryClient = useQueryClient();
  const user = useBoundStore((s) => s.user);
  const tenant = useBoundStore((s) => s.tenant);
  const enabled = options?.enabled ?? true;

  // Connect and authenticate
  useEffect(() => {
    if (!enabled || !user?.id || !tenant?.id) {
      return;
    }

    const connect = async () => {
      try {
        const token = getAccessToken();
        await socketClient.authenticate({
          tenantId: tenant.id,
          userId: user.id,
          token: token ?? undefined,
        });
      } catch (error) {
        console.error('[Notifications] WebSocket connection failed:', error);
        // Only trigger auth failure for explicit authentication errors (401/403).
        // Infrastructure errors (502, network down) should NOT log the user out.
        const errorMsg = String(error);
        const isAuthError =
          errorMsg.includes('401') || errorMsg.includes('403') || errorMsg.includes('Unauthorized');
        if (isAuthError) {
          triggerAuthFailure();
        }
        // For non-auth errors (502, network), silently fail — notifications
        // will reconnect automatically when the service recovers.
      }
    };

    connect();

    return () => {
      socketClient.leave();
    };
  }, [enabled, user?.id, tenant?.id]);

  // Subscribe to notification events
  useEffect(() => {
    if (!enabled || !user?.id || !tenant?.id) {
      return;
    }

    // New notification
    const unsubNotification = socketClient.on('notification', (payload: NotificationPayload) => {
      // Defense in depth: strip HTML before pushing to the in-memory feed so
      // pre-fix DB rows or any regression in the backend strip cannot leak
      // markup into the bell drawer. See dev-reference/CRM_bugs.md R30-001.
      const plainTitle = stripHtml(payload.title);
      const plainDescription = stripHtml(payload.body);
      // Derive the navigation target from data entity ids so a click in the
      // drawer or the toast opens the right detail page. See R30-003.
      const actionUrl = deriveActionUrl(payload.data);
      addNotification({
        ...payload,
        title: plainTitle,
        body: plainDescription,
        ...(actionUrl ? { actionUrl } : {}),
      });

      toast(plainTitle, {
        description: plainDescription,
        action: actionUrl
          ? {
              label: 'View',
              onClick: () => {
                window.location.href = actionUrl;
              },
            }
          : undefined,
      });

      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    });

    // Notification count update
    const unsubCount = socketClient.on('notification:count', (payload) => {
      setUnreadCount(payload.unread);
    });

    // Notification read (from another tab/device)
    const unsubRead = socketClient.on('notification:read', () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    });

    // All notifications read
    const unsubReadAll = socketClient.on('notification:read_all', () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    });

    // Session events — handle expired, revoked, and concurrent login
    const unsubSession = socketClient.on('session:event', (payload: SessionPayload) => {
      const messages: Record<SessionPayload['type'], string> = {
        expired: 'Your session has expired. Please log in again.',
        revoked: 'Your session has been revoked by an administrator.',
        concurrent_login:
          'You have been logged out because your account was signed in from another location.',
      };
      toast.error(messages[payload.type] || payload.message);
      triggerAuthFailure();
    });

    return () => {
      unsubNotification();
      unsubCount();
      unsubRead();
      unsubReadAll();
      unsubSession();
    };
  }, [enabled, user?.id, tenant?.id, addNotification, setUnreadCount, queryClient]);

  // Return connection state
  return {
    isConnected: socketClient.isConnected,
    isAuthenticated: socketClient.authenticated,
  };
}

// =============================================================================
// COMBINED HOOK
// =============================================================================

/**
 * Combined hook for notifications - data, actions, and real-time
 */
export function useNotificationsWithRealtime(
  filters?: NotificationFilters,
  options?: NotificationQueryOptions,
) {
  const user = useBoundStore((s) => s.user);
  const tenant = useBoundStore((s) => s.tenant);
  const enabled = options?.enabled ?? Boolean(user?.id && tenant?.id);

  // Fetch notifications
  const query = useNotificationsQuery(filters, { enabled });

  // Set up real-time updates
  useRealTimeNotifications({ enabled });

  // Get store data and actions
  const notifications = useNotificationStore();
  const unreadCount = useUnreadNotificationCount();
  const actions = useNotificationActions();

  return {
    notifications,
    unreadCount,
    isLoading: query.isLoading,
    // `isFetching` is true during the FIRST load AND every subsequent
    // refetch; `isLoading` is only the first load. Expose both so the
    // refresh button can show a spinner on every refresh, not just the
    // initial render. See R30-003.
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isEnabled: enabled,
    ...actions,
  };
}
